import { ConfigFromServer, Flow, TealiumProfileFromServer } from '../@types/config'
import { Intent } from '../@types/intent'
import { getBasicLeadTrackingType, hashStreetValue, hashValue, TrackingEventType } from './tracking-utils'
import { Lead, OptionalLeadSource } from '../@types/lead'
import { User } from '../@types/user'
import { PropertyLocation } from '../@types/location'
import { Address } from '../@types/address'

declare global {
  interface Window {
    IS24?: {
      TEALIUM?: {
        tracking: {
          report: ReportFn
        }
      }
      RLE?: {
        configuration?: ConfigFromServer
        optionalTealiumProfile?: TealiumProfileFromServer
        optionalLeadSource?: OptionalLeadSource
      }
    }
  }
}

const REPORT_TYPE_UTAG_VIEW = {
  reportType: 'UTAG_VIEW',
}

const MAX_TRACKING_RETRIES = 10
const TEALIUM_RETRY_INTERVAL = 1000
const PAGE_TITLE_PREFIX = 'is24.de.realtor.leadengine'
const PAGE_TITLE_SOLAR_PREFIX = 'is24.de.solar-leadengine'
const COUNTER_NAME = 'is24.de.realtor.leadengine.index'
const INITIAL_PAGE_VIEW = 'false'
const MODULE_NAME = 'realtor-leadengine'
const SOLAR_MODULE_NAME = 'solar-leadengine'
const SCRIPT_URL_REGEX =
  /^(?:https?:)?\/\/tracking\.(?:sandbox-)?immobilienscout24\.de(\/immobilienscout\/is24-de-satellite)?\/tr\.js.*/i
const scriptUrl = (countername: string, initialPageView: string, moduleName: string) =>
  `//tracking.${process.env.HOST}/tr.js?cP--countername=${countername}&cP--svc_module_name=${moduleName}&initialPageView=${initialPageView}`

const initDevTracking = () => {
  window.IS24 = window.IS24 || {
    TEALIUM: {
      tracking: {
        report: (event, args) => {
          console.log('reporting event', event, 'with args', args)
        },
      },
    },
  }
}

interface ReportingQueueElement {
  event: Record<string, string | null>
  args?: Record<string, string>
}

const reportingQueue: Array<ReportingQueueElement> = []

export const additionalCustomDimensions = async (user?: User, postcode?: string, street?: string) => {
  return {
    cf_e: await hashValue(user?.email),
    cf_p: await hashValue(user?.phoneNumber),
    cf_fn: await hashValue(user?.firstName),
    cf_ln: await hashValue(user?.lastName),
    cf_s: await hashStreetValue(street),
    cf_pc: await hashValue(postcode),
  }
}

const report = (event: Record<string, string | null>, args?: Record<string, string>): void => {
  reportingQueue.push({
    event,
    args,
  })

  if (process.env.STAGE === 'dev' && !window.IS24?.TEALIUM?.tracking?.report) {
    initDevTracking()
  }

  if (!window.IS24?.TEALIUM?.tracking?.report) {
    console.log('Tealium report function is not available')
    return
  }

  reportEventsQueue()
}

const reportEventsQueue = () => {
  let element = reportingQueue.shift()
  while (element && window.IS24?.TEALIUM?.tracking?.report) {
    window.IS24.TEALIUM.tracking.report(element.event, element.args)
    element = reportingQueue.shift()
  }
}

interface ReportViewEventArgs {
  pageTitle: string
  customDimensions?: Record<string, unknown>
  cooperationId?: string
}

function getPagPageTitle(args: ReportEventArgs) {
  const isExternalConfigurationPrefix = args.cooperationId?.includes('immoverkauf24') ? '.immoverkauf24' : ''
  return args.flow === Flow.SOLAR
    ? `${PAGE_TITLE_SOLAR_PREFIX}${isExternalConfigurationPrefix}.${args.pageTitle}`
    : `${PAGE_TITLE_PREFIX}${isExternalConfigurationPrefix}.${args.pageTitle}`
}

const reportViewEvent = async (
  args: ReportViewEventArgs,
  user?: User,
  postcode?: string,
  street?: string,
  flow?: Flow
): Promise<void> => {
  const event = {
    svc_module_name: flow === Flow.SOLAR ? SOLAR_MODULE_NAME : MODULE_NAME,
    pag_pagetitle:
      flow === Flow.SOLAR ? `${PAGE_TITLE_SOLAR_PREFIX}.${args.pageTitle}` : `${PAGE_TITLE_PREFIX}.${args.pageTitle}`,
  }

  report(
    { ...event, ...{ ...args.customDimensions, ...(await additionalCustomDimensions(user, postcode, street)) } },
    REPORT_TYPE_UTAG_VIEW
  )
}

interface ReportEventArgs extends ReportViewEventArgs {
  label: string
  inputValue: string
  eventName: string
  flow?: string
  action?: string
  category?: string
  isTrackingIntent?: boolean
}

export const reportSubmitLeadEvent = async (
  rawLeadId: string,
  eventName: string,
  lead?: Lead,
  user?: User,
  location?: PropertyLocation,
  address?: Address,
  flow?: string
): Promise<void> => {
  const trackingType = getBasicLeadTrackingType(lead?.intent)
  if (trackingType === TrackingEventType.BL) {
    await reportEvent(
      {
        pageTitle: `contactform`,
        action: 'homeowner',
        category: 'lead',
        label: 'basic',
        inputValue: '50',
        eventName: eventName,
        flow: flow ? flow?.toString() : 'rle',
        customDimensions: {
          evt_lafid: `${rawLeadId}`,
          evt_ga_value: '50',
          ga_cd_rle_intent: getCustomDimensionForIntent(lead?.intent),
          ga_cd_lead_engine_input_value: '',
        },
      },
      user,
      location?.postcode,
      address?.street
    )
  } else {
    await reportEvent(
      {
        pageTitle: `contactform`,
        eventName: eventName,
        flow: flow ? flow?.toString() : 'rle',
        action: 'realtor-leadengine',
        category: 'residential',
        label: 'request_basic',
        inputValue: '0',
        customDimensions: {
          evt_lafid: `${rawLeadId}`,
          evt_ga_value: '0',
          ga_cd_rle_intent: getCustomDimensionForIntent(lead?.intent),
          ga_cd_lead_engine_input_value: '',
        },
      },
      user,
      location?.postcode,
      address?.street
    )
  }
}

const reportEvent = async (args: ReportEventArgs, user?: User, postcode?: string, street?: string): Promise<void> => {
  const event = {
    evt_ga_label: args.label,
    pag_pagetitle: getPagPageTitle(args),
    evt_ga_action: args.action ? args.action : 'realtor-leadengine',
    evt_ga_category: args.category ? args.category : 'residential',
    svc_module_name: args.flow === Flow.SOLAR ? SOLAR_MODULE_NAME : MODULE_NAME,
    event_name: args.eventName,
    event_product: args.flow === Flow.SOLAR ? 'sle' : 'rle',
    event_parameter_1: args.eventName == 'lead_submit' ? args.label : args.pageTitle,
    event_parameter_2: args.inputValue,
    event_parameter_3: args.flow ? args.flow?.toString() : 'rle',
  }

  await report({
    ...event,
    ...{ ...args.customDimensions, ...(await additionalCustomDimensions(user, postcode, street)) },
  })
}

const reportEventSync = (args: ReportEventArgs) => {
  const event = {
    evt_ga_label: args.label,
    pag_pagetitle: getPagPageTitle(args),
    evt_ga_action: args.action ? args.action : 'realtor-leadengine',
    evt_ga_category: args.category ? args.category : 'residential',
    svc_module_name: MODULE_NAME,
  }

  report({
    ...event,
    ...args.customDimensions,
  })
}

const isTrackingScriptInDom = (): boolean => {
  const scriptElements = [...document.querySelectorAll('script')]
  return scriptElements.find((scriptElement) => SCRIPT_URL_REGEX.test(scriptElement.src)) !== undefined
}

enum TealiumState {
  INITIALIZED = 'INITIALIZED',
  UNINITIALIZED = 'UNINITIALIZED',
}

export const getCustomDimensionForIntent = (intent?: Intent, extendedBuyerLead?: boolean): string => {
  switch (intent) {
    case Intent.MAKLER_SALE:
      return 'sell_with-agent'
    case Intent.MAYBE_MAKLER_SALE:
      return 'sell_maybe-with-agent'
    case Intent.INDEPENDENT_SALE:
      return 'sell_without-agent'
    case Intent.INDEPENDENT_WITH_OFFER_SALE:
      return 'sell_without-agent_with-offer'
    case Intent.BUY:
      return extendedBuyerLead ? 'buy_extended' : 'buy'
    case Intent.BUY_WITH_SELL_OPTION:
      return 'buy_with-sell-option'
    case Intent.BUY_MAYBE_WITH_SELL_OPTION:
      return 'buy_maybe-with-sell-option'
    case Intent.RENT:
      return 'rent'
    case Intent.LET:
      return 'let'
    case Intent.PARTIAL_SALE:
      return 'sell_without-agent_partial'
    case Intent.MAKLER_LET:
      return 'let_with-agent'
    case Intent.MAYBE_MAKLER_LET:
      return 'let_maybe-with-agent'
    case Intent.INDEPENDENT_LET:
      return 'let_without-agent'
    default:
      return ''
  }
}

const getTealiumState = (retryTimeout = TEALIUM_RETRY_INTERVAL): Promise<TealiumState> => {
  return new Promise((resolve) => {
    if (window.IS24?.TEALIUM?.tracking?.report) {
      return resolve(TealiumState.INITIALIZED)
    }
    let intervalTriggerCounter = 0
    const interval = setInterval(() => {
      intervalTriggerCounter++
      if (intervalTriggerCounter <= MAX_TRACKING_RETRIES) {
        if (window.IS24?.TEALIUM?.tracking?.report) {
          clearInterval(interval)
          reportEventsQueue()
          resolve(TealiumState.INITIALIZED)
        }
      } else {
        clearInterval(interval)
        resolve(TealiumState.UNINITIALIZED)
      }
    }, retryTimeout)
  })
}

enum TrackingScriptState {
  EXISTS = 'EXISTS',
  ABSENT = 'ABSENT',
}

const getTrackingState = (): Promise<TrackingScriptState> => {
  if (isTrackingScriptInDom()) {
    return Promise.resolve(TrackingScriptState.EXISTS)
  }

  return getTealiumState(200).then((tealiumState) =>
    tealiumState === TealiumState.INITIALIZED ? TrackingScriptState.EXISTS : TrackingScriptState.ABSENT
  )
}

const buildScriptUrl = () => {
  const counterName = window.IS24?.RLE?.optionalTealiumProfile?.counterName
    ? window.IS24?.RLE?.optionalTealiumProfile?.counterName
    : COUNTER_NAME
  const moduleName = window.IS24?.RLE?.optionalTealiumProfile?.svcModuleName
    ? window.IS24?.RLE?.optionalTealiumProfile?.svcModuleName
    : MODULE_NAME
  const initialPageView = window.IS24?.RLE?.optionalTealiumProfile?.initialPageView
    ? window.IS24?.RLE?.optionalTealiumProfile?.initialPageView
    : INITIAL_PAGE_VIEW

  return `${scriptUrl(counterName, initialPageView, moduleName)}${
    window.IS24?.RLE?.optionalTealiumProfile?.addOnClick == 'true' ? '&addOnClick' : ''
  }${
    window.IS24?.RLE?.optionalTealiumProfile?.svcSatelliteWebsite
      ? '&cP--svc_satellite_website=' + window.IS24?.RLE?.optionalTealiumProfile?.svcSatelliteWebsite
      : ''
  }`
}

const addTrackingScript = (): void => {
  if (process.env.STAGE === 'dev') {
    console.log('Window IS24', window.IS24)
    return console.log('adding tracking script')
  }

  const script = document.createElement('script')
  script.src = buildScriptUrl()
  script.async = true
  document.body.appendChild(script)
}

export {
  reportViewEvent,
  ReportViewEventArgs,
  reportEvent,
  reportEventSync,
  ReportEventArgs,
  getTrackingState,
  TrackingScriptState,
  addTrackingScript,
  getTealiumState,
  TealiumState,
}
